import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { IDS } from '@/constants/testIds';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

function ForgotPasswordDialog({ open, onOpenChange, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset email');
    } finally { setBusy(false); }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setSent(false); setEmail(defaultEmail || ''); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Reset password</DialogTitle>
        </DialogHeader>
        {sent ? (
          <div className="py-4 space-y-3">
            <p className="serif text-lg">Check your inbox.</p>
            <p className="sans text-sm text-neutral-600">
              If <strong>{email}</strong> is on file we&rsquo;ve sent a link that&rsquo;s good for 60 minutes.
              Check spam if it doesn&rsquo;t show up in a minute.
            </p>
            <button type="button" onClick={close} className="btn-primary mt-2" data-testid="forgot-password-close">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 py-2">
            <p className="sans text-sm text-neutral-500">
              Enter the email on your IJE HAUS account and we&rsquo;ll send you a link to set a new password.
            </p>
            <div>
              <label className="ije-label">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@shop.com"
                className="ije-input"
                data-testid="forgot-password-email"
              />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <button type="button" onClick={close} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-10">Cancel</button>
              <button type="submit" disabled={busy || !email.trim()} className="btn-primary h-10" data-testid="forgot-password-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null} Send reset link
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const intended = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const next = new URLSearchParams(location.search).get('next');
    if (next) return navigate(next, { replace: true });
    if (intended && intended !== '/login') return navigate(intended, { replace: true });
    if (res.user.role === 'admin') navigate('/admin', { replace: true });
    else navigate('/dashboard', { replace: true });
  };

  return (
    <div className="container-x py-24 max-w-md">
      <span className="eyebrow">Trade Partners</span>
      <h1 className="h-display text-3xl md:text-4xl mt-4">Sign in.</h1>
      <p className="sans text-sm text-neutral-500 mt-3">
        Approved retailers only. <Link to="/apply" className="link-pink">Apply for an account</Link>.
      </p>

      <form onSubmit={submit} className="mt-12 space-y-6" data-testid={IDS.login.form}>
        <div>
          <label className="ije-label">Email</label>
          <input required type="email" data-testid={IDS.login.email} value={email} onChange={(e) => setEmail(e.target.value)} className="ije-input" placeholder="you@shop.com" />
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <label className="ije-label">Password</label>
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className="sans text-[11px] tracking-widest uppercase text-neutral-500 hover:text-[color:var(--ije-pink-dark)]"
              data-testid="forgot-password-link"
            >
              Forgot?
            </button>
          </div>
          <input required type="password" data-testid={IDS.login.password} value={password} onChange={(e) => setPassword(e.target.value)} className="ije-input" />
        </div>
        {error && (
          <div className="sans text-sm" style={{ color: 'var(--ije-pink-dark)' }} data-testid={IDS.login.errorMessage}>
            {error}
          </div>
        )}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid={IDS.login.submit}>
          {busy ? 'Signing in…' : 'Sign In'}
        </button>
      </form>

      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} defaultEmail={email} />
    </div>
  );
}
